import { excludeUndefined } from "../common/utils/array.util";

/*
 * .filter predicate to exclude strings with less length than given argument
 */
export const exludeStringsBelowLengthOf =
    (minLength: number) =>
    (value: string): boolean =>
        value.length >= minLength;

/*
 * .filter predicate to exclude items with duplicate ids
 */
export function excludeDuplicatesById<T extends { id: string }>(
    value: T,
    index: number,
    self: T[],
): boolean {
    return index === self.findIndex((t) => t.id === value.id);
}

export function takeIfStringMatches<T>(
    filterSource: T[],
    compare: (value: T) => string | undefined,
    compareTo: string[],
) {
    return filterSource.filter((item) => {
        if (compareTo.length === 0) {
            return true;
        }

        const compareString = compare(item);
        return !compareString ? true : compareTo.includes(compareString);
    });
}

export function takeIfAnyStringMatches<T>(
    filterSource: T[],
    compare: (value: T) => string[] | undefined,
    compareTo: string[],
) {
    return filterSource.filter((item) => {
        if (compareTo.length === 0) {
            return true;
        }

        const compareStrings = compare(item);

        return !compareStrings
            ? true
            : compareTo.filter((it) => compareStrings.includes(it)).length !== 0;
    });
}

export function takeIfAnyPredicateMatches<T>(
    compare: T[],
    stringToMatch: string,
    predicates: ((item: T) => string | undefined)[],
): T[] {
    return compare.filter(
        (it) =>
            predicates
                .map((predicate) => {
                    const predicateValue = predicate(it);

                    return predicateValue
                        ? predicateValue.toLowerCase()
                        : predicateValue;
                })
                .filter(excludeUndefined)
                .filter((matchString) => {
                    try {
                        return matchString.search(stringToMatch.toLowerCase()) > -1;
                    } catch {
                        return false;
                    }
                }).length !== 0,
    );
}

export function takeIfExistsTimes<T extends { id: string }>(
    existsTimes: number,
    items: T[],
) {
    return items
        .filter(
            (item) => existsTimes === items.filter((it) => it.id === item.id).length,
        )
        .filter(excludeDuplicatesById);
}

export function performOrOperatorSearch<T extends { id: string }>(
    onTerm: string,
    minimumCharLengthToSearch: number,
    predicates: ((item: T) => string | undefined)[],
    items: T[],
) {
    const separateTerms = onTerm
        .split(" ")
        .filter(exludeStringsBelowLengthOf(minimumCharLengthToSearch));

    // If no valid search terms, return empty array
    if (separateTerms.length === 0) {
        return [];
    }

    // Find items that match ANY search term (OR logic)
    return items
        .filter((item) => {
            // For each item, check if it matches ANY search term
            return separateTerms.some((term) => {
                // Check if any predicate for this item matches this search term
                return predicates.some((predicate) => {
                    const predicateValue = predicate(item);
                    const searchableValue = predicateValue?.toLowerCase();

                    if (!searchableValue) {
                        return false;
                    }

                    try {
                        return searchableValue.includes(term.toLowerCase());
                    } catch {
                        return false;
                    }
                });
            });
        })
        .filter(excludeDuplicatesById);
}
